/**
 * Lightweight CSS/SVG chart + visual primitives for the teacher dashboard, in the
 * learn.4klabs.ai editorial light/dark language (no chart library dependency).
 */

import type { ReactNode, CSSProperties } from 'react';
import { cn } from '../../../lib/utils';
import type { MasteryState, NextAction } from './mockData';

// ----------------------------------------------------------------------------
// Mastery palette
// ----------------------------------------------------------------------------

export const MASTERY_META: Record<MasteryState, { label: string; color: string; soft: string; text: string }> = {
  mastered: { label: 'Mastered', color: '#2F8F80', soft: 'rgba(47,143,128,0.16)', text: '#1F6B5F' },
  developing: { label: 'Developing', color: '#C99A3B', soft: 'rgba(201,154,59,0.18)', text: '#9A7320' },
  emerging: { label: 'Emerging', color: '#CB5B6B', soft: 'rgba(203,91,107,0.16)', text: '#B23B4E' },
  'not-attempted': { label: 'Not attempted', color: '#A9A7B4', soft: 'rgba(125,125,140,0.12)', text: '#8A8593' },
};
const MASTERY_ORDER: MasteryState[] = ['mastered', 'developing', 'emerging', 'not-attempted'];

export function MasteryLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1.5', className)}>
      {MASTERY_ORDER.map((s) => (
        <span key={s} className="flex items-center gap-1.5 text-[11.5px] font-medium text-[#56505F] dark:text-[#B2B0C0]">
          <i className="h-2.5 w-2.5 rounded-[3px]" style={{ background: MASTERY_META[s].color, opacity: s === 'not-attempted' ? 0.5 : 1 }} />
          {MASTERY_META[s].label}
        </span>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Layout primitives
// ----------------------------------------------------------------------------

export function Panel({ title, sub, right, children, className, bodyClassName }: {
  title?: ReactNode; sub?: ReactNode; right?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string;
}) {
  return (
    <div className={cn('rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]', className)}>
      {(title || right) && (
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            {title && <h3 className="font-serif text-[16px] font-semibold tracking-tight">{title}</h3>}
            {sub && <p className="mt-0.5 text-[12.5px] text-[#8A8593]">{sub}</p>}
          </div>
          {right}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

export function StatCard({ icon, label, value, sub, tone }: {
  icon?: ReactNode; label: string; value: ReactNode; sub?: ReactNode; tone?: 'default' | 'warn' | 'good';
}) {
  const toneText = tone === 'warn' ? 'text-[#B23B4E]' : tone === 'good' ? 'text-[#1F6B5F]' : '';
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-center gap-2 text-[#8A8593]">
        {icon && <span className="[&>svg]:h-[18px] [&>svg]:w-[18px] text-[#6D28D9] dark:text-[#B69BF5]">{icon}</span>}
        <span className="text-[12px] font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className={cn('mt-3 font-serif text-[30px] font-medium tracking-tight tabular-nums', toneText)}>{value}</div>
      {sub && <div className="mt-1 text-[12px] text-[#8A8593]">{sub}</div>}
    </div>
  );
}

export function Pill({ children, color, className, style }: { children: ReactNode; color?: string; className?: string; style?: CSSProperties }) {
  return (
    <span
      style={{ ...(color ? { color, borderColor: `${color}40`, background: `${color}14` } : undefined), ...style }}
      className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        !color && 'border-black/10 bg-black/[0.03] text-[#56505F] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#B2B0C0]', className)}
    >
      {children}
    </span>
  );
}

export function MasteryTag({ state }: { state: MasteryState }) {
  const m = MASTERY_META[state];
  return <Pill color={m.color}><i className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />{m.label}</Pill>;
}

// ----------------------------------------------------------------------------
// Charts
// ----------------------------------------------------------------------------

/** Horizontal stacked bar of the four mastery states. */
export function StackedMasteryBar({ mastered, developing, emerging, notAttempted, height = 12 }: {
  mastered: number; developing: number; emerging: number; notAttempted: number; height?: number;
}) {
  const total = mastered + developing + emerging + notAttempted || 1;
  const segs: Array<[MasteryState, number]> = [
    ['mastered', mastered], ['developing', developing], ['emerging', emerging], ['not-attempted', notAttempted],
  ];
  return (
    <div className="flex w-full overflow-hidden rounded-full" style={{ height }}>
      {segs.map(([s, v]) => v > 0 && (
        <div key={s} title={`${MASTERY_META[s].label}: ${v}`}
          style={{ width: `${(v / total) * 100}%`, background: MASTERY_META[s].color, opacity: s === 'not-attempted' ? 0.45 : 1 }} />
      ))}
    </div>
  );
}

/** Single progress bar. */
export function ProgressBar({ percent, color = '#2F8F80', height = 8 }: { percent: number; color?: string; height?: number }) {
  return (
    <div className="w-full overflow-hidden rounded-full bg-black/[0.06] dark:bg-white/[0.08]" style={{ height }}>
      <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, percent))}%`, background: color }} />
    </div>
  );
}

/** Conic-gradient donut with a centered figure. */
export function Donut({ percent, color = '#2F8F80', size = 132, label, caption }: {
  percent: number; color?: string; size?: number; label?: ReactNode; caption?: string;
}) {
  return (
    <div className="relative grid shrink-0 place-items-center rounded-full" style={{ height: size, width: size,
      background: `conic-gradient(${color} 0 ${percent}%, rgba(125,125,140,.15) ${percent}% 100%)` }}>
      <div className="absolute inset-[14px] rounded-full bg-white dark:bg-[#14141d]" />
      <div className="relative z-10 text-center">
        <b className="block font-serif text-[26px] font-medium leading-none tabular-nums">{label ?? `${percent}%`}</b>
        {caption && <span className="mt-1 block text-[10.5px] font-semibold uppercase tracking-wide text-[#8A8593]">{caption}</span>}
      </div>
    </div>
  );
}

/** SVG radar for the per-learner concept fingerprint (values 0..1). */
export function Radar({ axes, color = '#6D28D9', size = 280 }: {
  axes: Array<{ label: string; value: number }>; color?: string; size?: number;
}) {
  const cx = size / 2, cy = size / 2, r = size / 2 - 44;
  const n = axes.length;
  const angle = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i: number, v: number) => [cx + Math.cos(angle(i)) * r * v, cy + Math.sin(angle(i)) * r * v] as const;
  const rings = [0.25, 0.5, 0.75, 1];
  const poly = (v: (i: number) => number) => axes.map((_, i) => point(i, v(i)).join(',')).join(' ');
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%" role="img" aria-label="Concept fingerprint">
      {rings.map((rr) => (
        <polygon key={rr} points={poly(() => rr)} fill="none" stroke="currentColor" className="text-black/10 dark:text-white/10" strokeWidth="1" />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="currentColor" className="text-black/10 dark:text-white/10" strokeWidth="1" />;
      })}
      <polygon points={poly((i) => axes[i].value)} fill={`${color}28`} stroke={color} strokeWidth="2" strokeLinejoin="round" />
      {axes.map((a, i) => {
        const [x, y] = point(i, a.value);
        return <circle key={i} cx={x} cy={y} r={3} fill={color} />;
      })}
      {axes.map((a, i) => {
        const [x, y] = point(i, 1.16);
        return (
          <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            className="fill-[#56505F] text-[9.5px] font-medium dark:fill-[#B2B0C0]">{a.label}</text>
        );
      })}
    </svg>
  );
}

// ----------------------------------------------------------------------------
// Action severity
// ----------------------------------------------------------------------------

export const SEVERITY_META: Record<NextAction['severity'], { label: string; color: string }> = {
  high: { label: 'High priority', color: '#CB5B6B' },
  medium: { label: 'Recommended', color: '#C99A3B' },
  low: { label: 'Opportunity', color: '#2F8F80' },
};

export const KIND_LABEL: Record<NextAction['kind'], string> = {
  reteach: 'Re-teach', practice: 'Practice', enrich: 'Enrich', engage: 'Re-engage',
};

export const masteryColor = (s: MasteryState) => MASTERY_META[s].color;
